<template>
  <div class="c-general-tab__section" v-if="displayFieldMappingSection">
    <h3>Field Mapping</h3>
    <div class="flex gap-8">
      <BittsButton
        v-if="displayMapOwnerButton"
        text="Map Account Owner"
        variant="outline"
        size="small"
        data-testid="map-ae-btn"
        @click="showMapNewColumns"
      />
      <BittsButton
        v-if="!disableMapDuns"
        text="Map DUNS"
        variant="outline"
        size="small"
        data-testid="duns-btn"
        @click="showMapDuns"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import {
  BIG_CSV_FILE_LIMIT_500MB,
  BQ_CSV_ACCOUNT_EXECUTIVE_FIELDS,
  TEMP_DUNS,
} from '@/constants/feature_flags';
import { useFeatureFlagStore, useSourcesStore } from '@/stores';
import { Source } from '@/types/sources';

const router = useRouter();
const sourcesStore = useSourcesStore();

const { hasFeatureFlag } = useFeatureFlagStore();
const { hasPermission } = useAuth();
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const { isCSVFeed, isGSheetFeed, source, sourceDisabled } =
  useDataSourceSettings();

const hasDunsNumber = computed(() => hasFeatureFlag(TEMP_DUNS));
const hasBqCsvAEFields = computed(() =>
  hasFeatureFlag(BQ_CSV_ACCOUNT_EXECUTIVE_FIELDS),
);
const hasBigCSVFileLimit = computed(() =>
  hasFeatureFlag(BIG_CSV_FILE_LIMIT_500MB),
);

function hasMappedAEData(source: Source) {
  return sourcesStore
    .getRelatedSources(source)
    .some((source) => source.mdm_type === 'user');
}

const displayMapOwnerButton = computed(
  () =>
    (isCSVFeed.value || isGSheetFeed.value) &&
    !sourceDisabled.value &&
    !(source.value ? hasMappedAEData(source.value) : true) &&
    !hasBqCsvAEFields.value &&
    !hasBigCSVFileLimit.value,
);

const disableMapDuns = computed(() => {
  if (!source.value) return true;
  const hasDunsAlreadyMapped = source.value.fields.some(
    (field) => field.mdm_property === 'company_duns_number',
  );
  const excludedColumns = [
    'File Name',
    'Upload Time',
    'id',
    'createdAt',
    'created_at',
  ];
  const hasPotentialDunsFields = source.value.fields.some(
    (field) =>
      field.is_visible &&
      field.mdm_property === null &&
      !excludedColumns.includes(field.column),
  );

  return (
    !hasDunsNumber.value || hasDunsAlreadyMapped || !hasPotentialDunsFields
  );
});

const displayFieldMappingSection = computed(
  () =>
    canManageDataSources.value &&
    (displayMapOwnerButton.value || !disableMapDuns.value),
);

function showMapNewColumns() {
  router.push({
    name: 'map-ae-columns-settings',
  });
}

function showMapDuns() {
  router.push({
    name: 'map-duns-columns-settings',
  });
}
</script>
